import React, { useState, useCallback, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Mic,
  Building2,
  TrendingUp,
  Palette,
  ListTodo,
  Heart,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Brain,
  Sparkles,
  Crown,
  Shield,
  ImageIcon,
  Video,
  Plane,
  MapPin,
  Radio,
  Ticket,
  Map,
  Moon,
  Sun,
  Clapperboard,
  Search,
  Train,
  Car,
  Compass
} from 'lucide-react';
import { ThemeSelector } from '../ui/ThemeSelector';

const LOGO_URL = '/aira-echo-logo.png';
const LOGO_DARK_URL = '/aira-echo-logo.png'; // Same logo, will use CSS filter for light theme

// Navigation items - PRIORITY ORDER: AIRA Echo > AIRA Search > Travel ecosystem > Film Studio > Other features
const navItems = [
  // AIRA ECHO Social Platform (Top priority)
  { path: '/aira-echo', icon: Radio, label: 'AIRA ECHO Social', module: 'echo', isEcho: true, category: 'social' },
  
  // AIRA ECHO Search - AI-Powered Search Engine (High priority)
  { path: '/search', icon: Search, label: 'AIRA ECHO Search', module: 'search', isSearch: true, category: 'ai' },
  
  // TRAVEL ECOSYSTEM (Flagship products - High priority)
  { path: '/travel', icon: Plane, label: 'AIRA ECHO Travel', module: 'travel', category: 'travel', highlight: true },
  { path: '/trains', icon: Train, label: 'AIRA ECHO Trains', module: 'trains', category: 'travel', highlight: true },
  { path: '/stays', icon: Building2, label: 'AIRA ECHO Stay', module: 'stays', category: 'travel', highlight: true },
  { path: '/tours', icon: Compass, label: 'AIRA ECHO Tours', module: 'tours', category: 'travel', highlight: true },
  { path: '/experiences', icon: Ticket, label: 'AIRA ECHO Experiences', module: 'experiences', category: 'travel', highlight: true },
  { path: '/cabs', icon: Car, label: 'AIRA ECHO Cabs', module: 'cabs', category: 'travel', highlight: true },
  { path: '/insurance', icon: Shield, label: 'Travel Insurance', module: 'insurance', category: 'travel', highlight: true },
  { path: '/trip-planner', icon: Map, label: 'AIRA ECHO Trip Planner', module: 'trip_planner', category: 'travel', highlight: true },
  
  // FILM STUDIO - Create Your Own Film
  { path: '/film-studio', icon: Clapperboard, label: 'AIRA ECHO Studios', module: 'film_studio', category: 'creative', isFilm: true },
  
  // AI ASSISTANTS
  { path: '/assistant', icon: Brain, label: 'AIRA ECHO Assistant', module: 'assistant', category: 'ai' },
  { path: '/chat', icon: MessageSquare, label: 'AIRA ECHO Chat', module: 'chat', category: 'ai' },
  { path: '/voice', icon: Mic, label: 'AIRA ECHO Voice', module: 'voice', category: 'ai' },
  
  // CREATIVE & CONTENT
  { path: '/creator-mode', icon: Sparkles, label: 'Creator Mode', module: 'creator_mode', category: 'creative' },
  { path: '/creative', icon: Palette, label: 'Creative Studio', module: 'creative', category: 'creative' },
  { path: '/gallery', icon: ImageIcon, label: 'My Creations', module: 'gallery', category: 'creative' },
  { path: '/viral', icon: Heart, label: 'Viral Cards', module: 'viral', category: 'creative' },
  
  // BUSINESS & FINANCE
  { path: '/investment', icon: TrendingUp, label: 'Investment Guru', module: 'investment', category: 'business' },
  { path: '/business', icon: Building2, label: 'Business Builder', module: 'business', category: 'business' },
  { path: '/opportunities', icon: TrendingUp, label: 'Opportunity Radar', module: 'opportunities', category: 'business' },
  
  // LIFE & LEARNING
  { path: '/life-manager', icon: ListTodo, label: 'Life Manager', module: 'life_manager', category: 'life' },
  { path: '/life-coach', icon: Heart, label: 'Life Coach', module: 'life_coach', category: 'life' },
  { path: '/learning', icon: ListTodo, label: 'Learning Hub', module: 'learning', category: 'life' },
  { path: '/future-preview', icon: Sparkles, label: 'Future Preview', module: 'future_preview', category: 'life' },
  
  // COMMUNICATION
  { path: '/video-call', icon: Video, label: 'Video Call', module: 'video_call', category: 'communication' },
];

const bottomItems = [
  { path: '/subscription', icon: Crown, label: 'Subscription', highlight: true },
  { path: '/admin', icon: Shield, label: 'Admin Panel', adminOnly: true },
  { path: '/profile', icon: User, label: 'Profile' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

// Priority items (Echo + Search + Travel + Film Studio)
const priorityItems = navItems.slice(0, 7);
const otherItems = navItems.slice(7);

// Navigation Link Component
const NavItem = ({ item, collapsed, onClose }) => {
  const getClassName = useCallback(({ isActive }) => {
    if (item.isEcho) {
      return `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 mb-2 ${
        isActive 
          ? 'bg-gradient-to-r from-[#FFE135] to-[#FF3CAC] text-black font-semibold' 
          : 'bg-gradient-to-r from-[#FFE135]/20 to-[#FF3CAC]/20 text-[#FFE135] hover:from-[#FFE135]/30 hover:to-[#FF3CAC]/30'
      } ${collapsed ? 'justify-center px-3' : ''}`;
    }
    if (item.isSearch) {
      return `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 mb-2 ${
        isActive 
          ? 'bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white font-semibold shadow-lg shadow-purple-500/30' 
          : 'bg-gradient-to-r from-[#8B5CF6]/20 to-[#06B6D4]/20 text-[#8B5CF6] hover:from-[#8B5CF6]/30 hover:to-[#06B6D4]/30 border border-[#8B5CF6]/30'
      } ${collapsed ? 'justify-center px-3' : ''}`;
    }
    if (item.isFilm) {
      return `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 mb-2 ${
        isActive 
          ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold shadow-lg shadow-purple-500/30' 
          : 'bg-gradient-to-r from-[#9333EA]/20 to-[#EC4899]/20 text-[#EC4899] hover:from-[#9333EA]/30 hover:to-[#EC4899]/30 border border-[#9333EA]/30'
      } ${collapsed ? 'justify-center px-3' : ''}`;
    }
    if (item.category === 'travel') {
      return `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
        isActive 
          ? 'bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-accent)]/20 text-[var(--color-primary)] border-l-2 border-[var(--color-primary)] font-medium' 
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)] hover:text-[var(--color-primary)]'
      } ${collapsed ? 'justify-center px-3' : ''}`;
    }
    return `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-3' : ''}`;
  }, [item, collapsed]);

  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      onClick={onClose}
      className={getClassName}
      data-testid={`nav-${item.module}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="whitespace-nowrap overflow-hidden"
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
    </NavLink>
  );
};

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { logout, user } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed(prev => !prev);
  }, [setCollapsed]);

  const openMobile = useCallback(() => {
    setMobileOpen(true);
  }, []);

  // Render sidebar content
  const sidebarContent = useMemo(() => (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div className="p-4 flex items-center gap-3">
        <img 
          src={LOGO_URL} 
          alt="AIRA ECHO Logo" 
          className={`w-10 h-10 object-contain transition-all ${!isDark ? 'brightness-0 saturate-100' : ''}`}
          style={!isDark ? { filter: 'invert(15%) sepia(30%) saturate(1000%) hue-rotate(220deg)' } : {}}
        />
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="overflow-hidden"
            >
              <h1 className="font-heading font-bold text-xl whitespace-nowrap text-[var(--text-primary)]">AIRA ECHO</h1>
              <p className="text-xs text-[var(--text-tertiary)] whitespace-nowrap">AI Super App</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Collapse Button - Desktop only */}
      <button
        onClick={toggleCollapse}
        className="hidden lg:flex items-center justify-center w-8 h-8 absolute -right-4 top-6 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-full hover:bg-[var(--bg-card-hover)] transition-colors z-10"
        data-testid="sidebar-collapse-btn"
      >
        <ChevronLeft className={`w-4 h-4 text-[var(--text-secondary)] transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
      </button>

      {/* Theme Toggle - Only when not collapsed */}
      {!collapsed && (
        <div className="px-4 pb-2">
          <ThemeSelector variant="compact" />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-hide">
        {/* Priority Section: Echo + Travel */}
        <div className="space-y-1">
          {priorityItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={collapsed} onClose={closeMobile} />
          ))}
        </div>
        
        {/* Divider */}
        {!collapsed && (
          <div className="py-3">
            <div className="h-px bg-[var(--border-color)]" />
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-3 mb-2 px-3">
              AI Tools
            </p>
          </div>
        )}
        
        {/* Other Items */}
        <div className="space-y-1">
          {otherItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={collapsed} onClose={closeMobile} />
          ))}
        </div>
      </nav>

      {/* Bottom Section */}
      <div className="px-3 py-4 border-t border-[var(--border-color)] space-y-1">
        {/* Quick Theme Toggle - Only when collapsed */}
        {collapsed && (
          <button
            onClick={toggleTheme}
            className="sidebar-link justify-center w-full mb-2"
            data-testid="sidebar-theme-toggle"
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5 text-[var(--color-gold)]" />}
          </button>
        )}
        
        {bottomItems.map((item) => {
          // Hide admin panel if user is not admin
          if (item.adminOnly && user?.role !== 'admin') return null;
          
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeMobile}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-3' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="whitespace-nowrap overflow-hidden"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </NavLink>
          );
        })}
        
        {/* Logout - Only show if user is authenticated */}
        {user && (
          <button
            onClick={handleLogout}
            className={`sidebar-link w-full text-[var(--color-error)] hover:bg-[var(--color-error)]/10 ${collapsed ? 'justify-center px-3' : ''}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="whitespace-nowrap overflow-hidden"
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        )}

        {/* User Info */}
        {user && !collapsed && (
          <div className="pt-3 px-2">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
                  <span className="text-sm font-medium text-white">{user.name?.[0]?.toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-[var(--text-primary)]">{user.name}</p>
                <p className="text-xs text-[var(--text-tertiary)] truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ), [collapsed, closeMobile, handleLogout, isDark, toggleCollapse, toggleTheme, user]);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={openMobile}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[var(--bg-card)] backdrop-blur-xl rounded-xl border border-[var(--border-color)] shadow-lg"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6 text-[var(--text-primary)]" />
      </button>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={closeMobile}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed left-0 top-0 h-full w-[300px] bg-[var(--bg-primary)] backdrop-blur-2xl border-r border-[var(--border-color)] z-50 shadow-2xl"
            >
              <button
                onClick={closeMobile}
                className="absolute top-4 right-4 p-2 hover:bg-[var(--bg-card-hover)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[var(--text-primary)]" />
              </button>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 80 : 256 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="hidden lg:block fixed left-0 top-0 h-full bg-[var(--bg-primary)]/95 backdrop-blur-2xl border-r border-[var(--border-color)] z-40"
      >
        {sidebarContent}
      </motion.aside>
    </>
  );
};
